import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../../auth/AuthContext'
import config from '../../../config'
import type {
    ExcelImportFieldStatus,
    ExcelImportProcessItem,
    ExcelImportProcessItemField,
    ExcelImportProcessRequest,
    ExcelImportResponse,
    StorageFile,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke, { LONG_TIMEOUT_MS } from '../../../utils/APIInvoke'
import { fileTimestamp } from '../../../utils/dates'
import {
    errorMessage,
    pageWindow,
    POLICY_IMPORT_EXCEL_RESULT_PATH,
    POLICY_IMPORT_PATH,
    statusOf,
} from '../common/policy-import-common'
import type { ExcelResultLocationState } from '../common/policy-import-common'

export type ExcelColumnKey = Exclude<keyof ExcelImportProcessItem, 'rowId' | 'polizaId' | 'status'>

export interface ExcelColumn {
    key: ExcelColumnKey
    label: string
    width: number
    align?: 'right'
}

export interface ExcelCell {
    value: string
    status: ExcelImportFieldStatus
    message: string
}

export interface ExcelRow {
    id: string
    status: ExcelImportFieldStatus
    cells: Record<ExcelColumnKey, ExcelCell>
}

export interface ExcelSummary {
    ok: number
    warning: number
    error: number
}

export const TEMPLATE_URL = '/downloads/polizas-import-template.xlsx'
export const TEMPLATE_DOWNLOAD_NAME = 'formato-importacion-polizas.xlsx'
export const EXCEL_ACCEPT = '.xlsx'
export const PAGE_SIZE = 20

const PROCESS_ERROR = 'No se pudo procesar el Excel.'
const UPLOAD_ERROR = 'No se pudo subir el archivo.'
const IMPORT_ERROR = 'Error al importar las pólizas.'

export const INSTRUCTIONS = [
    'Descarga el formato de importación.',
    'Llena el formato respetando las columnas definidas.',
    'Selecciona el formato de fecha que usaste y carga el archivo.',
    'Valida la información antes de realizar la importación definitiva.',
    'Da clic en "Importar pólizas" para comenzar el proceso.',
]

export const DATE_FORMATS: Array<{ group: string; options: Array<{ value: string; label: string }> }> = [
    {
        group: 'Formatos sin hora',
        options: [
            { value: 'dd/MM/yyyy', label: 'dd/MM/aaaa · 30/12/2023' },
            { value: 'MM/dd/yyyy', label: 'MM/dd/aaaa · 12/30/2023' },
            { value: 'yyyy/MM/dd', label: 'aaaa/MM/dd · 2023/12/30' },
            { value: 'yyyy/dd/MM', label: 'aaaa/dd/MM · 2023/30/12' },
            { value: 'dd-MM-yyyy', label: 'dd-MM-aaaa · 30-12-2023' },
            { value: 'MM-dd-yyyy', label: 'MM-dd-aaaa · 12-30-2023' },
            { value: 'yyyy-MM-dd', label: 'aaaa-MM-dd · 2023-12-30' },
            { value: 'yyyy-dd-MM', label: 'aaaa-dd-MM · 2023-30-12' },
        ],
    },
    {
        group: 'Formatos con hora',
        options: [
            { value: 'dd/MM/yyyy hh:mm:ss', label: 'dd/MM/aaaa hh:mm:ss · 30/12/2023 00:00:00' },
            { value: 'MM/dd/yyyy hh:mm:ss', label: 'MM/dd/aaaa hh:mm:ss · 12/30/2023 00:00:00' },
            { value: 'yyyy/MM/dd hh:mm:ss', label: 'aaaa/MM/dd hh:mm:ss · 2023/12/30 00:00:00' },
            { value: 'yyyy/dd/MM hh:mm:ss', label: 'aaaa/dd/MM hh:mm:ss · 2023/30/12 00:00:00' },
            { value: 'dd-MM-yyyy hh:mm:ss', label: 'dd-MM-aaaa hh:mm:ss · 30-12-2023 00:00:00' },
            { value: 'MM-dd-yyyy hh:mm:ss', label: 'MM-dd-aaaa hh:mm:ss · 12-30-2023 00:00:00' },
            { value: 'yyyy-MM-dd hh:mm:ss', label: 'aaaa-MM-dd hh:mm:ss · 2023-12-30 00:00:00' },
            { value: 'yyyy-dd-MM hh:mm:ss', label: 'aaaa-dd-MM hh:mm:ss · 2023-30-12 00:00:00' },
        ],
    },
]

export const COLUMNS: ExcelColumn[] = [
    { key: 'noPoliza', label: 'No. póliza', width: 150 },
    { key: 'claveAgente', label: 'Clave agente', width: 130 },
    { key: 'formaPago', label: 'Forma de pago', width: 130 },
    { key: 'ramo', label: 'Ramo', width: 110 },
    { key: 'concepto', label: 'Concepto', width: 180 },
    { key: 'paquete', label: 'Paquete / plan', width: 150 },
    { key: 'fechaVigenciaDe', label: 'Vigencia inicio', width: 130 },
    { key: 'fechaVigenciaA', label: 'Vigencia fin', width: 130 },
    { key: 'moneda', label: 'Moneda', width: 90 },
    { key: 'prima', label: 'Prima', width: 120, align: 'right' },
    { key: 'derecho', label: 'Derecho', width: 110, align: 'right' },
    { key: 'recargo', label: 'Recargo', width: 110, align: 'right' },
    { key: 'iva', label: 'IVA', width: 110, align: 'right' },
    { key: 'total', label: 'Total', width: 120, align: 'right' },
    { key: 'comision', label: 'Comisión', width: 110, align: 'right' },
    { key: 'personalidad', label: 'Personalidad', width: 120 },
    { key: 'rfc', label: 'RFC', width: 150 },
    { key: 'nombres', label: 'Nombres', width: 240 },
    { key: 'apPaterno', label: 'Apellido paterno', width: 160 },
    { key: 'apMaterno', label: 'Apellido materno', width: 160 },
    { key: 'fechaNacimiento', label: 'Nacimiento / registro', width: 170 },
]

const toCell = (field?: ExcelImportProcessItemField | null): ExcelCell => ({
    value: field?.value ?? '',
    status: statusOf(field?.status),
    message: field?.errorMessage || '',
})

const rowStatus = (item: ExcelImportProcessItem): ExcelImportFieldStatus => statusOf(item.status?.status || item.status?.value)

const toRow = (item: ExcelImportProcessItem, index: number): ExcelRow => ({
    id: item.rowId?.value || String(index),
    status: rowStatus(item),
    cells: Object.fromEntries(COLUMNS.map((column) => [column.key, toCell(item[column.key])])) as Record<ExcelColumnKey, ExcelCell>,
})

export const usePolicyImportExcelModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { user } = useAuth()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || POLICY_IMPORT_PATH

    const [dateFormat, setDateFormat] = useState(DATE_FORMATS[0].options[0].value)
    const [fileImport, setFileImport] = useState<ExcelImportProcessRequest | null>(null)
    const [originalName, setOriginalName] = useState('')
    const [items, setItems] = useState<ExcelImportProcessItem[] | null>(null)
    const [processing, setProcessing] = useState(false)
    const [importing, setImporting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [showIssues, setShowIssues] = useState(false)
    const [page, setPage] = useState(1)

    const rows = useMemo(() => (items || []).map(toRow), [items])
    const summary = useMemo<ExcelSummary>(() => rows.reduce<ExcelSummary>((acc, row) => ({ ...acc, [row.status]: acc[row.status] + 1 }), { ok: 0, warning: 0, error: 0 }), [rows])

    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
    const currentPage = Math.min(page, totalPages)
    const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

    const uploadExcel = async (file?: File | null) => {
        if (!file || processing) return
        try {
            setProcessing(true)
            setError(null)
            setItems(null)
            setPage(1)
            const fileName = `${fileTimestamp()}-${file.name}`
            const body = new FormData()
            body.append('bucket', config.bucket.name)
            body.append('name', fileName)
            body.append('path', `agentes/${user?.agenteId}/excel-import`)
            body.append('file', file)
            const uploaded: WrapperResponse<StorageFile> = await (await APIInvoke.uploadStorage(body)).json()
            if (uploaded.ok !== true || !uploaded.body) throw new Error(uploaded.message || UPLOAD_ERROR)

            const request: ExcelImportProcessRequest = { fileName, fileUrl: uploaded.body.url, dateFormat }
            setFileImport(request)
            setOriginalName(file.name)
            const response: WrapperResponse<ExcelImportProcessItem[]> = await (await APIInvoke.invokePOST('/excel-importer/process', request, { timeoutMs: LONG_TIMEOUT_MS })).json()
            if (!response.ok) throw new Error(response.message || PROCESS_ERROR)
            const processed = response.body || []
            setItems(processed)
            if (processed.some((item) => rowStatus(item) !== 'ok')) setShowIssues(true)
        } catch (uploadError) {
            console.error(uploadError)
            setItems(null)
            setFileImport(null)
            setError(errorMessage(uploadError, PROCESS_ERROR))
        } finally {
            setProcessing(false)
        }
    }

    const importPolicies = async () => {
        if (!fileImport || summary.error > 0 || importing) return
        try {
            setImporting(true)
            setError(null)
            const response: WrapperResponse<ExcelImportResponse[]> = await (await APIInvoke.invokePOST('/excel-importer/importar', fileImport, { timeoutMs: LONG_TIMEOUT_MS })).json()
            if (!response.ok) throw new Error(response.message || IMPORT_ERROR)
            history.push({ pathname: POLICY_IMPORT_EXCEL_RESULT_PATH, state: { polizas: response.body || [] } satisfies ExcelResultLocationState })
        } catch (importError) {
            console.error('Error al importar las pólizas', importError)
            setError(errorMessage(importError, IMPORT_ERROR))
        } finally {
            setImporting(false)
        }
    }

    return {
        dateFormat,
        rows: pageRows,
        totalRows: rows.length,
        hasData: items !== null,
        originalName,
        summary,
        processing,
        importing,
        busy: processing || importing,
        error,
        showIssues,
        canImport: Boolean(fileImport) && rows.length > 0 && summary.error === 0 && !importing,
        page: currentPage,
        totalPages,
        pages: rows.length > 0 ? pageWindow(currentPage, totalPages) : [],
        rangeFrom: rows.length === 0 ? 0 : ((currentPage - 1) * PAGE_SIZE) + 1,
        rangeTo: rows.length === 0 ? 0 : ((currentPage - 1) * PAGE_SIZE) + pageRows.length,
        backTo: backUrl,
        changeDateFormat: setDateFormat,
        uploadExcel,
        importPolicies,
        setPage,
        closeIssues: () => setShowIssues(false),
    }
}
